/**
 * @fileoverview Admin Authentication Hook
 * 
 * Checks if the current user has admin role.
 * Uses server-side role validation via has_role database function.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function useAdminAuth() {
  // V-020 fix: Use realUser (actual authenticated user) instead of user
  // (which returns the impersonated user during impersonation).
  // This prevents the admin from being locked out of admin routes while impersonating.
  const { user, realUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!realUser) {
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    // Check admin role via database function using the real (non-impersonated) user ID
    // Using type assertion since has_role is a custom function not in auto-generated types
    (supabase.rpc as any)('has_role', { _user_id: realUser.id, _role: 'admin' })
      .then(({ data, error }: { data: boolean | null; error: any }) => {
        if (error) {
          console.error('Error checking admin role:', error);
          setIsAdmin(false);
        } else {
          setIsAdmin(data === true);
        }
        setIsLoading(false);
      });
  }, [realUser, authLoading]);

  return { isAdmin, isLoading, user };
}
